<script setup lang="ts">
import { computed, ref } from 'vue';
import { composerRows } from '../lib/nyx-presentation.js';
import { nyxMessageError } from '../lib/nyx-actions.js';
import AppIcon from './AppIcon.vue';

/**
 * The docked input of the chat, in the shape of Claude Code's or Codex's prompt
 * box. Enter sends; Shift+Enter adds a line. While the client works the send
 * button becomes Stop so the person always has one obvious control.
 */
const props = defineProps<{
  disabled: boolean;
  canSend: boolean;
  running: boolean;
  canStop: boolean;
  hint: string;
}>();
const emit = defineEmits<{ send: []; stop: [] }>();
const text = defineModel<string>({ required: true });
const input = ref<HTMLTextAreaElement>();
const limit = 16000;
const rows = computed(() => composerRows(text.value));
const bytes = computed(() => new TextEncoder().encode(text.value).length);
const messageError = computed(() => nyxMessageError(text.value, true));
const ready = computed(() => props.canSend && !props.disabled && !nyxMessageError(text.value));
function submit() {
  if (ready.value) emit('send');
}
function onEnter(event: KeyboardEvent) {
  if (event.shiftKey || event.isComposing) return;
  event.preventDefault();
  submit();
}
defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <form class="nyx-composer" :data-disabled="disabled ? 'true' : 'false'" @submit.prevent="submit">
    <div class="nyx-composer-row">
      <span class="nyx-composer-caret" aria-hidden="true">›</span>
      <textarea
        ref="input"
        v-model="text"
        class="nyx-composer-input"
        :rows="rows"
        :maxlength="limit"
        :disabled="disabled"
        aria-label="Message"
        placeholder="Message the client…"
        @keydown.enter="onEnter"
      ></textarea>
      <button
        v-if="running"
        type="button"
        class="nyx-composer-send is-stop"
        :disabled="!canStop"
        title="Interrupt the current turn"
        @click="emit('stop')"
      >
        <span class="nyx-stop-glyph" aria-hidden="true" />Stop
      </button>
      <button
        v-else
        type="submit"
        class="nyx-composer-send primary"
        :disabled="!ready"
        title="Send (Enter)"
      >
        <AppIcon name="chevronUp" /><span class="sr-only">Send</span>
      </button>
    </div>
    <div class="nyx-composer-foot">
      <div class="nyx-composer-chips"><slot name="chips" /></div>
      <span class="nyx-composer-hint">{{ messageError || hint }}</span>
      <span v-if="bytes > limit * 0.75" class="nyx-composer-count mono"
        >{{ bytes }} / {{ limit }} bytes</span
      >
    </div>
  </form>
</template>
